import { useEffect, useState, type ReactNode } from "react";

import { api, useAlCambiar, useDatos } from "../api";
import { useAccion } from "../componentes/acciones";
import { Carga } from "../componentes/Carga";
import { TarjetaMensaje } from "../componentes/TarjetaMensaje";
import { Alert, Badge, Button, Card, EmptyState, Heading, Icon, Page, Select, Tabs, Text, TopBar } from "../ds";
import { ESTADOS_APERTURA, numero } from "../formato";
import { usable, usePlantillaElegida } from "../plantillaElegida";
import { Enlace, navegar, useUbicacion } from "../router";
import { useSesion } from "../sesion";
import type { Apertura, Aperturas, Cola as DatosCola, Zona } from "../tipos";
import s from "./Cola.module.css";

type Pestana = "pendientes" | "descartados";

export function Cola() {
  const { params } = useUbicacion();
  const pestana = (params.get("pestana") === "descartados" ? "descartados" : "pendientes") as Pestana;
  // Los enviados se siguen en el pipeline; los enlaces viejos a esa pestaña van allá.
  useEffect(() => {
    if (params.get("pestana") === "enviados") navegar("/pipeline", { reemplazar: true });
  }, [params]);
  const { aperturas, elegida, elegir } = usePlantillaElegida();
  // La vista previa de los pendientes se arma con la plantilla elegida: hasta saber cuál es, no se pide.
  const listaLista = aperturas !== undefined;
  const { datos, error, cargando, recargar } = useDatos<DatosCola>(
    listaLista ? `/api/cola?pestana=${pestana}${elegida ? `&plantilla=${encodeURIComponent(elegida.nombre)}` : ""}` : null,
  );
  const { sesion } = useSesion();
  useAlCambiar(recargar);

  const chatwoot = sesion?.integraciones.chatwoot;
  const c = datos?.contadores;

  return (
    <>
      <TopBar
        title="Cola de WhatsApp"
        subtitle="Borradores listos para mandar, ordenados por puntaje."
        actions={
          sesion ? (
            <>
              {chatwoot?.configurado && chatwoot.bandeja ? (
                <Badge tone="success" size="md" dot>
                  Chatwoot conectado
                </Badge>
              ) : (
                <Badge tone="warning" size="md" dot>
                  Chatwoot sin configurar
                </Badge>
              )}
              <Badge tone="neutral" size="md" icon="file-text">
                {sesion.envio.modo === "plantilla" ? `Plantilla ${elegida?.nombre ?? sesion.envio.plantilla?.nombre}` : "Texto libre"}
              </Badge>
            </>
          ) : null
        }
      />
      <Page width="none">
        {sesion && !(chatwoot?.configurado && chatwoot.bandeja) ? (
          <Alert tone="warning" title="Chatwoot todavía no está conectado">
            Mientras tanto, abrí cada mensaje en wa.me, mandalo desde el WhatsApp del equipo y marcalo como enviado. Para conectarlo, mirá{" "}
            <Enlace href="/configuracion">Configuración</Enlace>.
          </Alert>
        ) : sesion?.envio.modo === "plantilla" ? (
          <Alert tone="info" title="El primer mensaje sale como plantilla aprobada por Meta">
            La API oficial de WhatsApp sólo deja abrir una conversación con una plantilla. Elegí cuál en el panel de la derecha: cada envío completa tu nombre y la zona de la inmobiliaria, marcados en violeta, y el resto del texto es fijo.
          </Alert>
        ) : sesion ? (
          <Alert tone="warning" title="Texto libre">
            Cada mensaje sale como está escrito. Si la bandeja es la API oficial de WhatsApp, Meta rechaza el primer mensaje que no sea plantilla: configurá WHATSAPP_PLANTILLA. Con un número no oficial, mandá pocos por día.
          </Alert>
        ) : null}

        <div className={s.columnas}>
          <section className={s.lista} aria-label="Mensajes">
            <Tabs
              value={pestana}
              onChange={(v) => navegar(v === "pendientes" ? "/cola" : `/cola?pestana=${v}`, { reemplazar: true })}
              tabs={[
                { value: "pendientes", label: "Pendientes", count: c?.pendientes, countTone: "warning" },
                { value: "descartados", label: "Descartados", count: c?.descartados },
              ]}
            />
            {!datos ? (
              <Carga error={error} onReintentar={recargar} filas={6} />
            ) : datos.mensajes.length === 0 ? (
              <EmptyState
                icon={pestana === "pendientes" ? "inbox" : "trash-2"}
                title={pestana === "pendientes" ? "No quedan borradores pendientes" : "No hay descartados en los últimos 30 días"}
                tone="dashed"
              >
                {pestana === "pendientes" ? "Prepará más desde el panel de la derecha." : null}
              </EmptyState>
            ) : (
              <div className={s.tarjetas} aria-busy={cargando}>
                {datos.mensajes.map((m) => (
                  <TarjetaMensaje
                    key={m.id}
                    m={m}
                    modo={datos.modo}
                    plantilla={elegida?.nombre ?? null}
                    onCambio={recargar}
                  />
                ))}
              </div>
            )}
          </section>

          <aside className={s.lateral} aria-label="Resumen y acciones">
            {sesion?.envio.modo === "plantilla" ? <ElegirPlantilla aperturas={aperturas} elegida={elegida} onElegir={elegir} /> : null}
            <Card padding="md">
              <div className={s.cabecera}>
                <Heading level={2} size="sm">
                  Hoy
                </Heading>
              </div>
              <dl className={s.kpis}>
                <Kpi etiqueta="Enviados" valor={c?.enviadosHoy} />
                <Kpi etiqueta="En cola" valor={c?.pendientes} />
                <Kpi etiqueta="Respuestas humanas" valor={c?.respuestasHoy} />
              </dl>
            </Card>
            <Preparar disponibles={c?.disponibles} minimo={datos?.minimoWhatsapp ?? 60} onListo={recargar} />
            <Card padding="md">
              <Heading level={2} size="sm">
                Qué pasa después de enviar
              </Heading>
              <ul className={s.pasos}>
                <Paso icono="send" tono="brand">
                  La conversación queda abierta en Chatwoot y el lead pasa a contactada.
                </Paso>
                <Paso icono="message-circle" tono="success">
                  Cuando contesta una persona, Chatwoot avisa y pasa sola a “Respondió” en el <Enlace href="/pipeline">Pipeline</Enlace>. Un aviso automático de WhatsApp Business no la mueve: sigue esperando y puede recibir el seguimiento.
                </Paso>
                <Paso icono="user-x" tono="danger">
                  Si pide que no le escriban más, marcalo “No le interesa” en el pipeline: queda fuera de todo contacto.
                </Paso>
              </ul>
            </Card>
          </aside>
        </div>
      </Page>
    </>
  );
}

/* Con qué apertura sale el próximo Enviar. Sólo se puede elegir una aprobada por Meta; las
 * demás se ven apagadas con el motivo, para que se note si falta aprobar una. */
function ElegirPlantilla({ aperturas, elegida, onElegir }: { aperturas: Aperturas | undefined; elegida: Apertura | null; onElegir: (nombre: string) => void }) {
  const lista = aperturas?.lista ?? [];
  const estado = elegida ? ESTADOS_APERTURA[elegida.estado ?? "sin_confirmar"] : null;
  return (
    <Card padding="md">
      <div className={s.preparar}>
        <div>
          <Heading level={2} size="sm">
            Plantilla del primer mensaje
          </Heading>
          <Text size="xs" tone="muted">
            Vale para todos los envíos de esta cola hasta que la cambies. El tablero compara cómo rinde cada una.
          </Text>
        </div>
        {!aperturas ? (
          <Text size="sm" tone="muted">
            Buscando las plantillas…
          </Text>
        ) : lista.length === 0 ? (
          <Text size="sm" tone="muted">
            No hay aperturas configuradas.
          </Text>
        ) : (
          <>
            <Select
              label="Apertura"
              value={elegida?.nombre ?? ""}
              onChange={(e) => onElegir(e.target.value)}
              options={lista.map((a) => ({
                value: a.nombre,
                label: a.nombre,
                description: usable(a) ? a.descripcion : ESTADOS_APERTURA[a.estado ?? "sin_confirmar"].etiqueta,
                disabled: !usable(a),
              }))}
            />
            {elegida && estado ? (
              <Badge tone={estado.tono} size="sm" dot>
                {estado.etiqueta}
              </Badge>
            ) : null}
            {aperturas.error ? (
              <Alert tone="warning" compact>
                No se pudo consultar Chatwoot: {aperturas.error}
              </Alert>
            ) : null}
            {lista.length === 1 ? (
              <Text size="xs" tone="muted">
                Para probar otra apertura, agregala a la lista de <code>APERTURAS_WHATSAPP</code> en el código y cargala en WhatsApp Manager.
              </Text>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}

function Kpi({ etiqueta, valor }: { etiqueta: string; valor: number | undefined }) {
  return (
    <div className={s.kpi}>
      <dt>{etiqueta}</dt>
      <dd>{valor === undefined ? "–" : numero(valor)}</dd>
    </div>
  );
}

function Paso({ icono, tono, children }: { icono: "send" | "message-circle" | "user-x"; tono: "brand" | "success" | "danger"; children: ReactNode }) {
  return (
    <li className={s.paso}>
      <span className={s.pasoIcono} data-tono={tono}>
        <Icon name={icono} size={14} />
      </span>
      <Text as="span" size="sm">
        {children}
      </Text>
    </li>
  );
}

function Preparar({ disponibles, minimo, onListo }: { disponibles: number | undefined; minimo: number; onListo: () => void }) {
  const { datos: zonas } = useDatos<Zona[]>("/api/zonas");
  const { pendiente, ejecutar } = useAccion();
  const [cantidad, setCantidad] = useState("20");
  const [zona, setZona] = useState("");
  const [piso, setPiso] = useState(String(minimo));

  const preparar = async () => {
    const r = await ejecutar(
      "preparar",
      () => api<{ borradores: number }>("/api/cola/preparar", { metodo: "POST", cuerpo: { cantidad: Number(cantidad), zonaId: zona || null, minimo: Number(piso) } }),
      (x) => (x.borradores ? `Se prepararon ${x.borradores} borradores` : "No había inmobiliarias con esos filtros"),
    );
    if (r) onListo();
  };

  const conWhatsapp = (zonas ?? []).filter((z) => z.conWhatsapp > 0).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  return (
    <Card padding="md">
      <div className={s.preparar}>
        <Heading level={2} size="sm">
          Preparar más borradores
        </Heading>
        <Text size="sm">
          {disponibles === undefined ? (
            "Calculando cuántas quedan…"
          ) : (
            <>
              <strong className={s.cifra}>{numero(disponibles)}</strong> inmobiliarias calificadas tienen WhatsApp móvil, puntaje de {minimo} o más y todavía ningún borrador.
            </>
          )}
        </Text>
        <Select
          label="Cantidad"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          options={["10", "20", "50"].map((v) => ({ value: v, label: `${v} borradores` }))}
        />
        <Select
          label="Zona"
          value={zona}
          onChange={(e) => setZona(e.target.value)}
          options={[{ value: "", label: "Todas las zonas" }, ...conWhatsapp.map((z) => ({ value: z.id, label: z.nombre, detail: String(z.conWhatsapp) }))]}
        />
        <Select
          label="Puntaje mínimo"
          value={piso}
          onChange={(e) => setPiso(e.target.value)}
          options={[minimo, 70, 80]
            .filter((v, i, a) => a.indexOf(v) === i && v >= minimo)
            .map((v) => ({ value: String(v), label: `${v} o más` }))}
        />
        <Button fullWidth icon="plus" pending={pendiente === "preparar"} onClick={preparar} disabled={disponibles === 0}>
          Preparar borradores
        </Button>
      </div>
    </Card>
  );
}
